import type { PageDataMap, SlotGroupOverridesMap } from '@vibesites/runtime';
import chainPagesData from '../vibesites-pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Navigation eligibility for this site
// ----------------------------------------------------------------------------
// `vibesites-pages.json` carries the full inherited page catalog (names, paths,
// positions); this site's `pages.json` holds the editor's per-page overrides
// (e.g. visible: false for pages set to not show in navigation). The local
// overrides are layered last so nav always honors them, even when the
// generated catalog is older than the latest page edit.
// ============================================================================

/** Slot-group path for the Property Map page image (SiteMapImage › image). */
export const SITE_MAP_IMAGE_GROUP_PATH = 'sitemapimage.image';

/**
 * True when any scope has an uploaded Property Map image. Overrides for
 * SiteMapImage are saved on the `/property-map` page, so callers must scan
 * the full override map (not only the current page's scoped resolve) or the
 * header on other pages would miss the upload.
 */
export function hasSiteMapImageOverride(
  overrides: SlotGroupOverridesMap | undefined | null,
): boolean {
  if (!overrides) return false;
  for (const [key, override] of Object.entries(overrides)) {
    const path = key.includes('::') ? key.slice(key.lastIndexOf('::') + 2) : key;
    if (path !== SITE_MAP_IMAGE_GROUP_PATH) continue;
    const field = override?.fieldOverrides?.image;
    const value = field?.customValue ?? field?.generatedValue;
    if (value && String(value).trim()) return true;
  }
  return false;
}

export interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  label: string;
  href: string;
  /** Open in a new tab for off-site destinations (OLL, resident portal). */
  target?: string;
  /** Optional catalog event for off-site CTAs (see tracking-events). */
  event?: 'apply_now_clicked' | 'resident_portal_clicked';
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

/** Page aliases that should never appear in the main navigation (see the
    Haven Realty Capital theme: Apply Now / Resident Login link out instead,
    and the header CTA covers Schedule a Tour). */
export const NAV_EXCLUDED_ALIASES = new Set<string>([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'apply-now',
  'resident-access',
]);

/** Sub-nav nesting from the Haven Realty Capital theme. The generated page
    catalog doesn't carry `parent`, so it's restated here; a `parent` set in
    this site's pages.json still wins. */
const DEFAULT_NAV_PARENTS: Record<string, string> = {
  'interactive-map': 'floor-plans',
  'site-map': 'floor-plans',
  'pet-policy': 'amenities',
};

function mergedNavPages(): Record<string, PageEntry> {
  const chain = chainPagesData as PageDataMap;
  const local = localPagesData as PageDataMap;
  const aliases = new Set([...Object.keys(chain), ...Object.keys(local)]);
  const result: Record<string, PageEntry> = {};

  for (const alias of aliases) {
    const merged = { ...(chain[alias] || {}), ...(local[alias] || {}) };
    if (merged.deleted_at) continue;
    result[alias] = {
      name: merged.name ?? alias,
      path: merged.path ?? `/${alias}`,
      position: merged.position ?? 0,
      visible: merged.visible,
      template: merged.template,
      noindex: merged.noindex,
      parent: (merged as { parent?: string }).parent ?? DEFAULT_NAV_PARENTS[alias],
    };
  }

  return result;
}

const pagesData = mergedNavPages();

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
export function normalizePath(p: string): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

function isNavListable(
  alias: string,
  page: PageEntry,
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): boolean {
  if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
  if (page.visible === false) return false;
  if (page.template) return false;
  if (alias === 'virtual-tour' && !hasVirtualTours) return false;
  if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
  if (alias === 'site-map' && !hasSiteMapImage) return false;
  return true;
}

/** Position-sorted `[alias, page]` entries eligible for the primary nav. */
export function getEligibleNavPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): Array<[string, PageEntry]> {
  return (Object.entries(pagesData) as Array<[string, PageEntry]>)
    .filter(([alias, page]) =>
      isNavListable(alias, page, hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage),
    )
    .sort(([, a], [, b]) => a.position - b.position);
}

export interface ExternalNavCtas {
  /** Online leasing (OLL) / apply URL — opens in a new tab. */
  applyNowUrl?: string | null;
  /** Resident portal URL — opens in a new tab. */
  residentPortalUrl?: string | null;
  applyNowLabel?: string;
  residentPortalLabel?: string;
}

/**
 * Build navigation links from the merged page catalog. Pages declaring
 * `parent: <alias>` are nested as `children` of their parent entry; orphaned
 * children (parent missing or hidden) fall back to the top level. External
 * Apply Now / Resident Login CTAs are appended after Contact Us.
 */
export function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
  externalCtas?: ExternalNavCtas,
): NavLink[] {
  const eligible = getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage);
  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  const extras: NavLink[] = [];
  const applyUrl = externalCtas?.applyNowUrl?.trim();
  if (applyUrl) {
    extras.push({
      label: externalCtas?.applyNowLabel || 'Apply Now',
      href: applyUrl,
      target: '_blank',
      event: 'apply_now_clicked',
    });
  }
  const portalUrl = externalCtas?.residentPortalUrl?.trim();
  if (portalUrl) {
    extras.push({
      label: externalCtas?.residentPortalLabel || 'Resident Login',
      href: portalUrl,
      target: '_blank',
      event: 'resident_portal_clicked',
    });
  }

  if (extras.length === 0) return topLevel;

  const contactIdx = topLevel.findIndex(
    (l) => normalizePath(l.href) === '/contact-us' || l.label.toLowerCase() === 'contact us',
  );
  if (contactIdx >= 0) {
    topLevel.splice(contactIdx + 1, 0, ...extras);
    return topLevel;
  }
  return [...topLevel, ...extras];
}

/** Aliases kept out of the header link row (the logo covers Home) but still
    listed on the sitemap when visible in pages.json. */
const SITEMAP_EXTRA_ALIASES = ['home'];

/** Sitemap link list: header eligibility plus SITEMAP_EXTRA_ALIASES, without
    the outbound-only Apply Now / Resident Login CTAs. */
export function buildSitemapLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): NavLink[] {
  const extras: NavLink[] = SITEMAP_EXTRA_ALIASES.map((alias) => [alias, pagesData[alias]] as const)
    .filter(([, page]) => page && page.visible !== false && !page.template)
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({ label: page.name, href: page.path }));
  return [...extras, ...buildNavLinks(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage)];
}
